/**
 * ActivityTracker — persistent notification system for ongoing server-side actions.
 *
 * Actions (agent updates, test runs, emulator boots, etc.) appear as toast-like
 * notifications. When minimized, a badge shows active count. Click to expand
 * and see all activities with progress. Completed ones can be dismissed.
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ActivityStatus = 'running' | 'done' | 'error';

export interface Activity {
  id: string;
  title: string;
  status: ActivityStatus;
  messages: Array<{ time: number; text: string }>;
  startedAt: number;
  finishedAt?: number;
}

interface ActivityContextType {
  activities: Activity[];
  add: (id: string, title: string) => void;
  update: (id: string, message: string) => void;
  finish: (id: string, status: 'done' | 'error', message?: string) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ActivityContext = createContext<ActivityContextType>({
  activities: [],
  add: () => {},
  update: () => {},
  finish: () => {},
  dismiss: () => {},
  dismissAll: () => {},
});

// Standard React Context pattern: hook + Provider co-located. Splitting
// into a separate file is the by-the-book fix for `react-refresh/only-
// export-components`, but the file rarely changes outside its component
// body so the Fast Refresh cost is theoretical.
// eslint-disable-next-line react-refresh/only-export-components
export function useActivity() {
  return useContext(ActivityContext);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ActivityProvider({ children }: { children: React.ReactNode }) {
  const [activities, setActivities] = useState<Activity[]>([]);

  const add = useCallback((id: string, title: string) => {
    setActivities(prev => {
      // Replace if exists
      const filtered = prev.filter(a => a.id !== id);
      return [...filtered, { id, title, status: 'running', messages: [], startedAt: Date.now() }];
    });
  }, []);

  const update = useCallback((id: string, message: string) => {
    setActivities(prev => prev.map(a =>
      a.id === id ? { ...a, messages: [...a.messages, { time: Date.now(), text: message }] } : a
    ));
  }, []);

  const finish = useCallback((id: string, status: 'done' | 'error', message?: string) => {
    setActivities(prev => prev.map(a =>
      a.id === id ? {
        ...a,
        status,
        finishedAt: Date.now(),
        messages: message ? [...a.messages, { time: Date.now(), text: message }] : a.messages,
      } : a
    ));
  }, []);

  const dismiss = useCallback((id: string) => {
    setActivities(prev => prev.filter(a => a.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setActivities(prev => prev.filter(a => a.status === 'running'));
  }, []);

  return (
    <ActivityContext.Provider value={{ activities, add, update, finish, dismiss, dismissAll }}>
      {children}
    </ActivityContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// UI — floating panel at bottom-right
// ---------------------------------------------------------------------------

export function ActivityPanel() {
  const { activities, dismiss, dismissAll } = useActivity();
  const [expanded, setExpanded] = useState(false);

  const running = activities.filter(a => a.status === 'running').length;
  const finished = activities.filter(a => a.status !== 'running').length;
  const total = activities.length;

  // Auto-expand when a new activity starts. Deferred via queueMicrotask so
  // the setState lands in a callback rather than the effect body.
  useEffect(() => {
    if (running > 0) queueMicrotask(() => setExpanded(true));
  }, [running]);

  if (total === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {/* Expanded panel */}
      {expanded && (
        <div className="w-80 max-h-80 bg-card border border-border rounded-lg shadow-xl overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/50">
            <span className="text-xs font-semibold">
              Activities
              {running > 0 && <span className="ml-1 text-blue-500">({running} running)</span>}
            </span>
            <div className="flex items-center gap-1">
              {finished > 0 && (
                <button className="text-[10px] text-muted-foreground hover:text-foreground" onClick={dismissAll}>
                  Clear done
                </button>
              )}
              <button className="text-muted-foreground hover:text-foreground ml-1" onClick={() => setExpanded(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </button>
            </div>
          </div>

          {/* Activity list */}
          <div className="overflow-y-auto flex-1 divide-y divide-border">
            {activities.map(a => (
              <ActivityItem key={a.id} activity={a} onDismiss={() => dismiss(a.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Minimized badge — always visible when there are activities */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border',
            running > 0
              ? 'bg-blue-500 text-white border-blue-600 animate-pulse'
              : 'bg-card text-foreground border-border'
          )}
        >
          {running > 0 ? (
            <>
              <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
              <span className="text-xs font-medium">{running} running</span>
            </>
          ) : (
            <span className="text-xs">{finished} completed</span>
          )}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single activity item
// ---------------------------------------------------------------------------

function ActivityItem({ activity, onDismiss }: { activity: Activity; onDismiss: () => void }) {
  const [showLog, setShowLog] = useState(false);
  // Tick once per second while still running so the "Xs" display is live.
  // `Date.now()` during render is impure (each render reads a different value
  // and React can render twice without a state change under StrictMode);
  // route it through state instead.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (activity.finishedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activity.finishedAt]);
  const elapsed = activity.finishedAt
    ? `${((activity.finishedAt - activity.startedAt) / 1000).toFixed(1)}s`
    : `${((now - activity.startedAt) / 1000).toFixed(0)}s`;

  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-2">
        {/* Status icon */}
        {activity.status === 'running' && (
          <span className="h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin shrink-0" />
        )}
        {activity.status === 'done' && (
          <span className="h-3 w-3 rounded-full bg-green-500 shrink-0" />
        )}
        {activity.status === 'error' && (
          <span className="h-3 w-3 rounded-full bg-red-500 shrink-0" />
        )}

        {/* Title + latest message */}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium truncate">{activity.title}</div>
          {activity.messages.length > 0 && (
            <div className="text-[10px] text-muted-foreground truncate">
              {activity.messages[activity.messages.length - 1].text}
            </div>
          )}
        </div>

        {/* Elapsed + actions */}
        <span className="text-[10px] text-muted-foreground shrink-0">{elapsed}</span>
        {activity.messages.length > 0 && (
          <button
            className="text-[10px] text-muted-foreground hover:text-foreground shrink-0"
            onClick={() => setShowLog(!showLog)}
          >
            {showLog ? 'hide' : 'log'}
          </button>
        )}
        {activity.status !== 'running' && (
          <button
            className="text-muted-foreground hover:text-foreground shrink-0"
            onClick={onDismiss}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        )}
      </div>

      {/* Log messages */}
      {showLog && activity.messages.length > 0 && (
        <pre className="mt-1 text-[10px] text-muted-foreground font-mono bg-muted/50 rounded p-1.5 max-h-32 overflow-y-auto">
          {activity.messages.map((m, i) => (
            <div key={i}>{m.text}</div>
          ))}
        </pre>
      )}
    </div>
  );
}
